// AUTO-GENERATED — do not edit. Source: /shared/. Run scripts/sync-shared.sh to update.
/**
 * SLIK — Canonical Availability Brain (2026-08-02)
 * =================================================
 * ONE source of truth for "is this barber working / how many slots are open / what's next".
 * Pure logic — NO React, NO Firebase. Each app loads its own barbers + appointments and
 * feeds them in. This is the module every surface (POS, customer-portal, public-booking)
 * must read from so availability can never drift again.
 *
 * DO NOT EDIT the per-app copies in <app>/src/shared/ — edit THIS file at repo root
 * and run scripts/sync-shared.sh to propagate.
 *
 * Background: barbers store their weekly schedule in TWO historical shapes —
 *   old: { available: bool, start, end }        new: { enabled: bool, start, end, breakStart }
 * Consumers that read only one flag broke for barbers on the other shape (every day "Full").
 * getDaySchedule() tolerates both.
 */

import { BookingRules, mergeBookingRules } from './bookingRules';

export type DayKey =
  | 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';

const DAY_KEYS: DayKey[] = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
];

/** A barber only needs an `availability` map to be scheduled here; everything else is optional. */
export interface AvailBarber {
  id?: string;
  status?: string;                       // 'active' | 'inactive' | ...
  availability?: Record<string, any> | null;
}

/** Minimal appointment shape needed for capacity + conflict math. */
export interface AvailAppointment {
  barberId: string;
  time?: string;                         // "10:30 AM" or "13:00" — both tolerated
  duration?: number;                     // minutes; defaults to 30
  status?: string;
}

export interface DaySchedule {
  working: boolean;
  start: number;                         // minutes from midnight
  end: number;
  breakStart: number | null;
  breakEnd: number | null;
}

/** The owner's shop hours for a day (from the public hours mirror). */
export interface ShopDayHours {
  closed?: boolean;
  open?: string;                         // "09:00"
  close?: string;                        // "13:00"
}

/** A one-off override for a specific calendar date (holiday closure / special hours). (Jose 2026-10-23) */
export interface SpecialDay {
  closed?: boolean;
  open?: string;                         // "09:00" — only when not closed
  close?: string;                        // "14:00"
  note?: string;                         // "Christmas Eve", "Staff training"
}
/** Map of YYYY-MM-DD → override. Lives in settings/shop + mirrored to publicConfig/hours. */
export type SpecialHours = Record<string, SpecialDay>;

/** Local YYYY-MM-DD for a Date, noon-anchored so a TZ never flips the calendar day. */
export function toYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * THE ONE-BRAIN for "what are the hours on THIS date": a special-date override (holiday closure or
 * custom hours) WINS over the weekly hours. Every surface that resolves a date's hours — staff
 * calendar/booking, portal + public booking, the phone receptionist — must route through this so a
 * day closed in Settings is closed everywhere. (Jose 2026-10-23)
 */
export function resolveDayHours(
  date: Date,
  weekly: ShopDayHours | undefined,
  special?: SpecialHours | null,
): ShopDayHours | undefined {
  const sp = special ? special[toYMD(date)] : undefined;
  if (sp) return sp.closed ? { closed: true } : { closed: false, open: sp.open, close: sp.close };
  return weekly;
}

/** A per-date SHIFT override for ONE barber on ONE date — from "Edit Shift" (the `shifts` collection).
 *  `isOff` wins; otherwise `startTime`/`endTime` ("HH:MM") replace that day's weekly hours. (Jose 2026-10-28) */
export interface ShiftOverride {
  isOff?: boolean;
  startTime?: string;   // "HH:MM" 24h
  endTime?: string;     // "HH:MM" 24h
}

/**
 * Fold a per-date SHIFT into a barber so the ONE availability brain honors "Edit Shift" WITHOUT any
 * engine-signature change: returns a shallow clone whose THAT-DATE weekly entry is replaced by the shift
 * (off, or new start/end). Every engine fn that already takes a barber (getDaySchedule / bookableSlots /
 * computeDayAvailability / isSlotBookable) then obeys the shift automatically — pass the folded barber.
 *
 * SAFETY:
 *  • PURE — no Firebase, no clock. `date.getDay()` is the LOCAL weekday (same basis getDaySchedule uses),
 *    so the shift lands on the day the staff edited — never a UTC-rolled neighbor.
 *  • Shift hours are wall-clock "HH:MM" strings parsed the SAME way as weekly hours (parseTimeToMinutes),
 *    so no Date/UTC conversion ever happens; and getDaySchedule STILL intersects with shop hours after,
 *    so a shift can't open the shop past its own hours.
 *  • `shift` null/undefined → returns the barber UNCHANGED (identical to today's behavior).
 */
export function applyShift(
  barber: AvailBarber | null | undefined,
  date: Date,
  shift?: ShiftOverride | null,
): AvailBarber | null | undefined {
  if (!barber || !shift) return barber;
  const dayKey = DAY_KEYS[date.getDay()];
  const cur: any = (barber.availability && (barber.availability as any)[dayKey]) || {};
  const on = !shift.isOff;
  return {
    ...barber,
    availability: {
      ...(barber.availability || {}),
      [dayKey]: {
        ...cur,
        enabled: on,
        available: on,
        ...(shift.startTime ? { start: shift.startTime } : {}),
        ...(shift.endTime ? { end: shift.endTime } : {}),
      },
    },
  };
}

/**
 * Canonical "is this barber working this day?" read for a single day's record.
 * `enabled` is the CANONICAL field; `available` is the deprecated legacy mirror kept
 * only so old data/writers stay compatible. Every reader should go through this so the
 * schema stays in one place. Missing day -> defaults to working (matches getDaySchedule).
 */
export function isDayEnabled(
  day: { enabled?: boolean; available?: boolean } | null | undefined,
): boolean {
  if (!day) return true;
  return day.enabled ?? day.available ?? true;
}

/**
 * Convenience: is this barber scheduled to work on this weekday at all? Pure weekday
 * lookup (ignores shop hours + appointments) — the exact signal the STAFF calendar uses
 * to gray out a barber's off-days. Customer surfaces (public/portal/family) call this to
 * gray out days a chosen barber isn't in, instead of showing slot counts. (Jose 2026-09-07)
 */
export function isBarberWorkingOn(
  barber: AvailBarber | null | undefined,
  date: Date,
): boolean {
  return isDayEnabled(barber?.availability?.[DAY_KEYS[date.getDay()]]);
}

export type DayStatus = 'off' | 'full' | 'open';

export interface DayAvailability {
  barberId?: string;
  status: DayStatus;                     // off = not scheduled, full = booked solid, open = has room
  working: boolean;
  totalSlots: number;
  bookedSlots: number;
  availableSlots: number;
  label: string;                         // "Off" | "Full" | "N open"
}

const SLOT_MINUTES = 30;

/** Statuses that actually occupy a chair (ignore cancelled / no-show). */
const ACTIVE_STATUSES = new Set([
  'confirmed', 'checked-in', 'in-progress', 'pending', 'scheduled', 'completed',
]);

/** Parse "10:30 AM", "9:00", "13:00", "00:00" → minutes from midnight. null if unusable. */
export function parseTimeToMinutes(t?: string | null): number | null {
  if (!t || typeof t !== 'string') return null;
  const s = t.trim();
  const ampm = /(am|pm)\s*$/i.exec(s);
  const core = ampm ? s.slice(0, ampm.index).trim() : s;
  const [hRaw, mRaw] = core.split(':');
  let h = Number(hRaw);
  const m = Number(mRaw ?? 0);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (ampm) {
    const pm = ampm[1].toLowerCase() === 'pm';
    if (pm && h !== 12) h += 12;
    if (!pm && h === 12) h = 0;
  }
  return h * 60 + m;
}

/**
 * Canonical read of a barber's schedule for a given date — tolerant of both stored shapes.
 * Falls back to sensible defaults (9–18, Sat 9–17) when a day is missing or malformed
 * (e.g. a bogus 00:00 start). Sunday defaults to open ONLY if the record says so.
 */
export function getDaySchedule(
  barber: AvailBarber | null | undefined,
  date: Date,
  shopHours?: ShopDayHours,
): DaySchedule {
  const raw: any = barber?.availability?.[DAY_KEYS[date.getDay()]];
  let working = raw ? (raw.enabled ?? raw.available ?? true) : true;
  const defStart = 9 * 60;
  const defEnd = date.getDay() === 6 ? 17 * 60 : 18 * 60;
  let start = parseTimeToMinutes(raw?.start);
  let end = parseTimeToMinutes(raw?.end);
  if (start == null || start === 0) start = defStart;   // bogus 00:00 → default
  if (end == null || end <= start) end = defEnd;
  const breakStart = parseTimeToMinutes(raw?.breakStart);
  const breakEnd = parseTimeToMinutes(raw?.breakEnd) ?? (breakStart != null ? breakStart + 60 : null);
  // Intersect with the owner's shop hours when provided: bookable = shop open AND barber working.
  // (Omit shopHours for per-barber-only behavior — e.g. internal views.)
  if (shopHours) {
    if (shopHours.closed) {
      working = false;
    } else {
      const so = parseTimeToMinutes(shopHours.open);
      const sc = parseTimeToMinutes(shopHours.close);
      if (so != null) start = Math.max(start, so);
      if (sc != null) end = Math.min(end, sc);
      if (end <= start) working = false;                // no overlap between shop + barber
    }
  }
  return { working, start, end, breakStart, breakEnd };
}

/** Bookable 30-min slots in a working window, minus any break. */
export function capacityForSchedule(sched: DaySchedule): number {
  if (!sched.working) return 0;
  let mins = Math.max(0, sched.end - sched.start);
  if (sched.breakStart != null && sched.breakEnd != null) {
    mins -= Math.max(0, Math.min(sched.breakEnd, sched.end) - Math.max(sched.breakStart, sched.start));
  }
  return Math.max(0, Math.floor(mins / SLOT_MINUTES));
}

/** Is a given minute-of-day inside the working window and NOT during a break? */
export function isWithinSchedule(minutes: number, sched: DaySchedule): boolean {
  if (!sched.working) return false;
  if (minutes < sched.start || minutes >= sched.end) return false;
  if (sched.breakStart != null && sched.breakEnd != null &&
      minutes >= sched.breakStart && minutes < sched.breakEnd) return false;
  return true;
}

/** How many slots a barber's appointments consume on a day. */
function bookedSlotCount(barberId: string | undefined, appts: AvailAppointment[]): number {
  let n = 0;
  for (const a of appts) {
    if (barberId != null && a.barberId !== barberId) continue;
    if (a.status && !ACTIVE_STATUSES.has(a.status)) continue;
    n += Math.ceil((a.duration || SLOT_MINUTES) / SLOT_MINUTES);
  }
  return n;
}

/** Human label for a day's status. */
export function statusLabel(a: Pick<DayAvailability, 'status' | 'availableSlots'>): string {
  if (a.status === 'off') return 'Off';
  if (a.status === 'full') return 'Full';
  return `${a.availableSlots} open`;
}

/**
 * THE core call: a barber's availability for one date, given that date's appointments.
 * `appts` may be pre-filtered to this barber+date or the full day's list (we filter by id).
 */
export function computeDayAvailability(
  barber: AvailBarber | null | undefined,
  date: Date,
  appts: AvailAppointment[],
  shopHours?: ShopDayHours,
): DayAvailability {
  const sched = getDaySchedule(barber, date, shopHours);
  const totalSlots = capacityForSchedule(sched);
  const bookedSlots = bookedSlotCount(barber?.id, appts);
  const availableSlots = Math.max(0, totalSlots - bookedSlots);
  const status: DayStatus = !sched.working ? 'off' : availableSlots <= 0 ? 'full' : 'open';
  const out: DayAvailability = {
    barberId: barber?.id,
    status,
    working: sched.working,
    totalSlots,
    bookedSlots,
    availableSlots,
    label: '',
  };
  out.label = statusLabel(out);
  return out;
}

/** Is a specific slot bookable for a barber on a date? (schedule + no appointment overlap) */
export function isSlotBookable(
  barber: AvailBarber | null | undefined,
  date: Date,
  slotMinutes: number,
  appts: AvailAppointment[],
  shopHours?: ShopDayHours,
  opts?: { noShowOccupies?: boolean; bufferMin?: number },
): boolean {
  const sched = getDaySchedule(barber, date, shopHours);
  if (!isWithinSchedule(slotMinutes, sched)) return false;
  // A no-show HOLDS its slot by default (Jose 2026-08-07 — the appointment is still on the board;
  // only a cancel frees it). Shops can flip this via BookingRules.noShowOccupiesSlot. Cancelled
  // never occupies. bufferMin extends each appointment's blocked window for cleanup/reset time.
  const noShowOccupies = opts?.noShowOccupies !== false; // default true
  const bufferMin = opts?.bufferMin || 0;
  for (const a of appts) {
    if (barber?.id != null && a.barberId !== barber.id) continue;
    const occupies = a.status
      ? (ACTIVE_STATUSES.has(a.status) || (a.status === 'no-show' && noShowOccupies))
      : true;
    if (!occupies) continue;
    const start = parseTimeToMinutes(a.time);
    if (start == null) continue;
    const end = start + (a.duration || SLOT_MINUTES) + bufferMin;
    if (slotMinutes >= start && slotMinutes < end) return false;
  }
  return true;
}

/**
 * Write-time double-book guard: does a requested [start, start+duration) window overlap
 * ANY active appointment for this barber on this day? Unlike isSlotBookable (which only
 * tests a single slot's START point), this tests the FULL requested duration — a 60-min
 * booking starting on a free 30-min slot can still overlap the NEXT appointment.
 *
 * End-exclusive overlap (matches the POS's hasConflictAcrossLocations):
 *   overlap  ⇔  !(reqEnd <= aptStart || reqStart >= aptEnd)
 *
 * `reqStartMinutes` = minute-of-day; `appts` = that barber's appointments for the date.
 * Emergency/squeeze bookings intentionally bypass this (they overlap on purpose).
 * Returns the conflicting appointment's time label (or true) when blocked, else false.
 */
export function hasApptConflict(
  barberId: string,
  reqStartMinutes: number,
  reqDuration: number,
  appts: AvailAppointment[],
  ignoreApptId?: string,
  blockNoShow: boolean = true,
): { conflict: true; withTime: string } | { conflict: false } {
  const reqEnd = reqStartMinutes + (reqDuration || SLOT_MINUTES);
  for (const a of appts) {
    if (a.barberId !== barberId) continue;
    if ((a as any).id != null && ignoreApptId != null && (a as any).id === ignoreApptId) continue;
    // Occupancy MUST match the picker (isSlotBookable): active statuses occupy, a no-show occupies
    // when the shop holds no-show slots (blockNoShow, default true = noShowOccupiesSlot); cancelled
    // never occupies. Previously no-shows were SKIPPED here, so a slot the picker would NOT offer
    // could still be booked over directly → overlaps. (2026-08-08)
    const occupies = !a.status || ACTIVE_STATUSES.has(a.status) || (a.status === 'no-show' && blockNoShow);
    if (!occupies) continue;
    const aStart = parseTimeToMinutes(a.time);
    if (aStart == null) continue;
    const aEnd = aStart + (a.duration || SLOT_MINUTES);
    if (!(reqEnd <= aStart || reqStartMinutes >= aEnd)) {
      return { conflict: true, withTime: a.time || minutesToLabel(aStart) };
    }
  }
  return { conflict: false };
}

/**
 * All bookable slot minutes for a barber on a date, given that date's appointments.
 * `nowRef` (optional, 2026-09-20): "what time is it" for the past-today guard. Browser callers
 * omit it (device clock = shop-local). SERVER callers (Cloud Functions run in UTC — the AI
 * receptionist tool layer) pass a Date carrying the SHOP's wall clock, else today's earlier
 * slots get pruned wrong by up to the UTC offset. Omitted → exact previous behavior.
 */
export function bookableSlots(
  barber: AvailBarber | null | undefined,
  date: Date,
  appts: AvailAppointment[],
  shopHours?: ShopDayHours,
  rules?: Partial<BookingRules>,
  nowRef?: Date,
  // (2026-10-22) SERVICE-FIT filter: when the caller knows the selected service's total
  // duration, drop any candidate where the FULL service won't fit — either it runs past the
  // barber's working end, or it would overlap the next appointment. isSlotBookable only tests
  // the START minute, so a 30-min service could be offered at 10:30 when the next cut is 10:45
  // (a 15-min gap) → the client picked it and hit "not enough time" at write. Using the SAME
  // hasApptConflict the write guard uses means the picker and the write agree exactly: every
  // slot shown WILL book. Omitted → previous start-point-only behavior (staff squeeze pickers).
  fitDurationMin?: number,
): number[] {
  const sched = getDaySchedule(barber, date, shopHours);
  if (!sched.working) return [];
  const R = mergeBookingRules(rules);
  const step = R.slotIntervalMin > 0 ? R.slotIntervalMin : SLOT_MINUTES;
  const opts = { noShowOccupies: R.noShowOccupiesSlot, bufferMin: R.bufferMin };

  // Candidate start times: the base grid, PLUS — when back-to-back is on — the exact moment the
  // barber frees up right after each occupying appointment (+ any cleanup buffer). That's what makes
  // an appointment ending at 10:15 offer 10:15, instead of waiting for the next 10:30 grid slot.
  const candidates = new Set<number>();
  for (let m = sched.start; m < sched.end; m += step) candidates.add(m);
  if (R.backToBack) {
    for (const a of appts) {
      if (barber?.id != null && a.barberId !== barber.id) continue;
      const occ = a.status
        ? (ACTIVE_STATUSES.has(a.status) || (a.status === 'no-show' && R.noShowOccupiesSlot))
        : true;
      if (!occ) continue;
      const s = parseTimeToMinutes(a.time);
      if (s == null) continue;
      const freeAt = s + (a.duration || SLOT_MINUTES) + R.bufferMin;
      if (freeAt >= sched.start && freeAt < sched.end) candidates.add(freeAt);
    }
  }

  // Past-of-TODAY guard (Jose 2026-08-12): a slot whose start time has already passed *today* is not
  // bookable — at 11:26 AM, 9:00–11:00 are gone; 11:30 onward stay. Future days are unaffected. Lives
  // in the shared brain so EVERY picker (POS rebook/reschedule, customer Book Now, family, public)
  // drops past times identically. (Emergency uses its own finder, which already filters past.)
  const now = nowRef ?? new Date();
  const isToday = date.getFullYear() === now.getFullYear()
    && date.getMonth() === now.getMonth()
    && date.getDate() === now.getDate();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  return Array.from(candidates)
    .sort((x, y) => x - y)
    .filter(m => !(isToday && m < nowMin))
    .filter(m => isSlotBookable(barber, date, m, appts, shopHours, opts))
    // SERVICE-FIT: the whole service must fit before close AND not overlap the next appt.
    .filter(m => {
      if (!fitDurationMin || fitDurationMin <= 0) return true;
      if (m + fitDurationMin > sched.end) return false;
      return !hasApptConflict(barber?.id ?? '', m, fitDurationMin, appts, undefined, R.noShowOccupiesSlot).conflict;
    });
}

export interface NextAvailable {
  date: Date;
  minutes: number;                       // minute-of-day of the slot
}

/**
 * Soonest bookable slot for a barber, scanning forward up to `horizonDays`.
 * `apptsForDate(date)` returns the appointments for a given day (yyyy-MM-dd),
 * letting the caller supply data however it loads it.
 */
export function findNextAvailable(
  barber: AvailBarber | null | undefined,
  fromDate: Date,
  apptsForDate: (date: Date) => AvailAppointment[],
  opts: {
    horizonDays?: number;
    afterMinutes?: number;
    shopHoursForDate?: (date: Date) => ShopDayHours;
  } = {},
): NextAvailable | null {
  const horizon = opts.horizonDays ?? 14;
  for (let i = 0; i < horizon; i++) {
    const day = new Date(fromDate);
    day.setDate(fromDate.getDate() + i);
    const appts = apptsForDate(day);
    const shopHours = opts.shopHoursForDate ? opts.shopHoursForDate(day) : undefined;
    const slots = bookableSlots(barber, day, appts, shopHours);
    const floor = i === 0 ? (opts.afterMinutes ?? 0) : 0;
    const hit = slots.find(m => m >= floor);
    if (hit != null) return { date: day, minutes: hit };
  }
  return null;
}

/** "570" → "9:30 AM" for display. */
export function minutesToLabel(minutes: number): string {
  const h24 = Math.floor(minutes / 60);
  const m = minutes % 60;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

/** ANY stored appointment time ("13:00" | "1:00 PM" | "8:30 AM" | "18:8") → "1:00 PM" for display.
 *  Tolerant of mixed 12h/24h storage (returns the input unchanged if unparseable). THE one helper every
 *  UI/notification surface should use so a user NEVER sees a 24-hour clock ("13:00"). Jose 2026-10-30:
 *  appointment `time` is being normalized to canonical 24h in storage; display stays 12h via this. */
export function formatTime12h(t?: string | null): string {
  const min = parseTimeToMinutes(t);
  return min == null ? (t || '') : minutesToLabel(min);
}
